import { useState, useMemo, FunctionComponent, SVGProps } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import settingsService from "api/settingsService";
import bankingService from "api/bankingService";
import { ICourtPortalSettings } from "dto/Settings/ICourtPortalSettings";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { authValidityLabel, isAuthExpired } from "utils/authValidity";
import CourtPortalSettings from "../CourtPortalSettings";
import ConfigureKSeF from "components/ConfigureKSeF";
import ConfigureEDelivery from "components/ConfigureEDelivery";
import ConfigureEpuap from "components/ConfigureEpuap";
import ConfigureUpe from "components/ConfigureUpe";
import AdeIcon from "assets/icons/ade.svg?react";
import EpuapLogo from "assets/icons/epuap.png";
import ConfigureProcurement from "components/ConfigureProcurement";
import procurementService from "api/procurementService";
import BankIntegrationWizard from "../BankIntegrationWizard";
import EmailIntegrationWizard from "../EmailIntegrationWizard";
import OfficeIntegrationWizard, {
  OfficeKind,
} from "../OfficeIntegrationWizard";
import IntegrationVideos from "../IntegrationVideos";
import PIIcon from "assets/icons/filters/pi.svg?react";
import KSeFIcon from "assets/icons/ksef.svg?react";
import InfoIcon from "assets/icons/info_icon.svg?react";
import OutlookIcon from "assets/icons/filters/outlook.svg?react";
import GoogleIcon from "assets/icons/filters/Google.svg?react";
import AppleIcon from "assets/icons/apple.svg?react";
import DocsIcon from "assets/icons/filters/docs.svg?react";
import MicrosoftIcon from "assets/icons/filters/Microsoft.svg?react";
import styles from "./styles.module.scss";

type SubTab = "active" | "public" | "nonpublic" | "email" | "office";

const SUB_TABS: { key: SubTab; label: string }[] = [
  { key: "active", label: "O integracjach" },
  { key: "email", label: "Email" },
  { key: "office", label: "Pakiety biurowe" },
  { key: "public", label: "Portale publiczne" },
  { key: "nonpublic", label: "Systemy bankowe" },
];

// Dostawcy, dla których istnieje już kreator umożliwiający rzeczywistą integrację.
type PublicProvider = "court" | "ksef" | "gov-edelivery" | "epuap" | "upe";

type SvgIcon = FunctionComponent<SVGProps<SVGSVGElement>>;

interface PublicIntegration {
  key: string;
  label: string;
  /** Syntetyczny opis zakresu usług udostępnianych w ramach integracji. */
  info: string;
  /** Logo portalu/usługi — jeśli dysponujemy zasobem. */
  Icon?: SvgIcon;
  /** Logo rastrowe (zbundlowany PNG) — użyj zamiast Icon, gdy logo nie jest SVG. */
  IconImg?: string;
  /**
   * Ustawione tylko gdy istnieje kreator integracji — wtedy pokazujemy przycisk
   * „Dodaj integrację". Dla pozostałych (sama kwerenda) przycisku nie dodajemy.
   */
  connectable?: PublicProvider;
  /**
   * Uwagi/adnotacje do integracji (np. „integracja częściowa" + zakres). Gdy
   * ustawione — w ostatniej kolumnie pokazujemy ikonę z tym tekstem w tooltipie.
   */
  notes?: string;
}

// Lista możliwych integracji w zakładce „Portale publiczne".
// Portale z publicznym API zebrane w kwerendzie (źródła w opisie zadania).
const PUBLIC_INTEGRATIONS: PublicIntegration[] = [
  {
    key: "court",
    label: "Portal Informacyjny sądów powszechnych",
    Icon: PIIcon,
    connectable: "court",
    info: "Integracja z Portalem Informacyjnym sądów powszechnych umożliwia automatyczne pobieranie doręczeń i pism sądowych, terminów posiedzeń i rozpraw oraz danych spraw (sygnatura, sąd, wydział, sędzia), a także bieżącą synchronizację statusów. Wymaga zalogowania danymi do PI.",
    notes: "Integracja częściowa. Zakres: pobieranie doręczeń, pism, terminów oraz danych spraw. Poza zakresem: składanie pism procesowych przez portal.",
  },
  {
    key: "ksef",
    label: "KSeF — Krajowy System e-Faktur",
    Icon: KSeFIcon,
    connectable: "ksef",
    info: "Integracja z Krajowym Systemem e-Faktur (KSeF) umożliwia wystawianie i wysyłanie faktur ustrukturyzowanych, pobieranie faktur zakupowych oraz urzędowych poświadczeń odbioru (UPO), a także synchronizację z rejestrem faktur. Wymaga uwierzytelnienia (NIP / token).",
    notes: "Integracja częściowa. Zakres: pobieranie i podgląd faktur, dekretacja/księgowanie oraz synchronizacja rejestru KSeF. Poza zakresem (w przygotowaniu): masowe wystawianie i wysyłka faktur ustrukturyzowanych.",
  },
  {
    key: "gov-edelivery",
    label: "e-Doręczenia (rządowe)",
    Icon: AdeIcon,
    connectable: "gov-edelivery",
    info: "Integracja z rządowymi e-Doręczeniami (Poczta Polska) umożliwia automatyczne pobieranie przesyłek przychodzących z Twojej skrzynki ADE, wraz z załącznikami i dowodami doręczenia (UPO/EPO), oraz wysyłanie przesyłek elektronicznych do adresatów z Bazy Adresów Elektronicznych (BAE). Wymaga własnego adresu ADE oraz certyfikatu klienta.",
  },
  {
    key: "epuap",
    label: "ePUAP",
    IconImg: EpuapLogo,
    connectable: "epuap",
    info: "Integracja z ePUAP umożliwia pobieranie przesyłek przychodzących ze skrytki oraz wysyłkę pism (skrytka/doręczyciel) wraz z urzędowymi poświadczeniami (UPP/UPD). Wymaga własnego identyfikatora podmiotu (EntityEID), adresu skrytki oraz certyfikatu klienta (PFX).",
  },
  {
    key: "upe",
    label: "UPE — Urzędowa Poczta Elektroniczna",
    connectable: "upe",
    info: "Integracja z Urzędową Pocztą Elektroniczną (UPE) umożliwia składanie wniosków o ogłoszenie aktów prawa miejscowego w wojewódzkim dzienniku urzędowym: paczka archiwalna z plikami ZIPX aktów jest podpisywana kwalifikowanym certyfikatem i przekazywana do redakcji dziennika. Zakres: wnioski o ogłoszenie i wycofanie aktu oraz wnioski do nadzoru RIO. Wymaga konta w UPE.",
  },
  {
    key: "procurement",
    label: "Elektroniczne zamówienia publiczne",
    info: "Integracja z Platformą e-Zamówienia (Urząd Zamówień Publicznych) — odczyt ogłoszeń o zamówieniach publicznych z Biuletynu Zamówień Publicznych (BZP WebService): przedmiot, zamawiający (nazwa i NIP), kody CPV, termin składania ofert oraz pełna treść ogłoszenia. Odczyt jest bezpłatny i nie wymaga uwierzytelnienia. Publikacja ogłoszeń, planów postępowań i sprawozdań (API MO/PP/MMIA, OAuth 2.0) — kolejny etap.",
  },
  {
    key: "krs",
    label: "KRS — Krajowy Rejestr Sądowy",
    info: "Otwarte API Krajowego Rejestru Sądowego (Ministerstwo Sprawiedliwości) — pobieranie odpisów aktualnych i pełnych podmiotów (spółki, fundacje, stowarzyszenia), danych reprezentacji i wspólników oraz dokumentów z Repozytorium Dokumentów Finansowych.",
  },
  {
    key: "ceidg",
    label: "CEIDG — Ewidencja Działalności Gospodarczej",
    info: "API Centralnej Ewidencji i Informacji o Działalności Gospodarczej — wyszukiwanie i pobieranie danych przedsiębiorców (jednoosobowe działalności): status, adres, kody PKD oraz daty rozpoczęcia, zawieszenia i zakończenia działalności.",
  },
  {
    key: "regon",
    label: "REGON — GUS (BIR)",
    info: "API REGON (GUS, Baza Internetowa REGON / BIR1) — pobieranie danych rejestrowych podmiotów gospodarki narodowej po NIP, REGON lub KRS: nazwa, adres, forma prawna, kody PKD.",
  },
  {
    key: "vat-whitelist",
    label: "Wykaz podatników VAT (Biała lista)",
    info: "API Wykazu podatników VAT (Biała lista, Ministerstwo Finansów) — weryfikacja statusu VAT kontrahenta oraz potwierdzenie zgodności numeru rachunku bankowego na wskazany dzień.",
  },
  {
    key: "vies",
    label: "VIES — VAT UE",
    info: "System VIES (Komisja Europejska) — weryfikacja aktywności numerów VAT UE kontrahentów z państw członkowskich Unii Europejskiej.",
  },
  {
    key: "crbr",
    label: "CRBR — Beneficjenci Rzeczywiści",
    info: "API Centralnego Rejestru Beneficjentów Rzeczywistych — pobieranie informacji o beneficjentach rzeczywistych podmiotów zobowiązanych do zgłoszenia.",
  },
  {
    key: "krz",
    label: "KRZ — Krajowy Rejestr Zadłużonych",
    info: "Krajowy Rejestr Zadłużonych (Ministerstwo Sprawiedliwości) — sprawdzanie informacji o postępowaniach upadłościowych, restrukturyzacyjnych i egzekucyjnych podmiotów oraz osób fizycznych.",
  },
  {
    key: "msig",
    label: "MSiG — Monitor Sądowy i Gospodarczy",
    info: "Monitor Sądowy i Gospodarczy — dostęp do ogłoszeń i obwieszczeń sądowych oraz gospodarczych dotyczących podmiotów wpisanych do KRS.",
  },
  {
    key: "egib",
    label: "EGiB — Ewidencja Gruntów i Budynków",
    info: "Ewidencja Gruntów i Budynków (GUGiK / Geoportal) — dostęp do danych ewidencyjnych przez usługi sieciowe WMS (Krajowa Integracja Ewidencji Gruntów) oraz zbiorcze WFS obejmujące wszystkie powiaty: działki, budynki, kontury i użytki gruntowe, geometria oraz identyfikatory ewidencyjne.",
  },
];

interface BankOption {
  key: string;
  label: string;
}

// 10 najpopularniejszych banków w Polsce (wg liczby klientów) — lista domyślna.
// Integracja w jednolitym standardzie PolishAPI (PSD2 / Open Banking).
const BANKS: BankOption[] = [
  { key: "pko", label: "PKO Bank Polski" },
  { key: "pekao", label: "Bank Pekao S.A." },
  // Santander Bank Polska → rebranding na Erste Bank Polska (kwiecień 2026).
  { key: "erste", label: "Erste Bank Polska" },
  { key: "mbank", label: "mBank" },
  { key: "ing", label: "ING Bank Śląski" },
  { key: "bnp", label: "BNP Paribas Bank Polska" },
  { key: "millennium", label: "Bank Millennium" },
  { key: "credit-agricole", label: "Credit Agricole Bank Polska" },
  { key: "alior", label: "Alior Bank" },
  { key: "velobank", label: "VeloBank" },
];

// Szerszy katalog aktywnych banków w UE do wyszukiwarki „wskaż swój bank".
// Docelowo pobierany z API agregatora (GoCardless/Nordigen — institutions).
const EU_BANKS: BankOption[] = [
  ...BANKS,
  // Pozostałe banki działające w Polsce
  { key: "citi", label: "Citi Handlowy (Polska)" },
  { key: "nest", label: "Nest Bank (Polska)" },
  { key: "pocztowy", label: "Bank Pocztowy (Polska)" },
  { key: "bos", label: "BOŚ Bank (Polska)" },
  { key: "sgb", label: "SGB-Bank (Polska)" },
  { key: "bps", label: "Bank Polskiej Spółdzielczości — BPS (Polska)" },
  { key: "toyota", label: "Toyota Bank (Polska)" },
  { key: "plus", label: "Plus Bank (Polska)" },
  { key: "aion", label: "Aion Bank (Polska/Belgia)" },
  { key: "revolut", label: "Revolut Bank (Litwa/UE)" },
  // Niemcy
  { key: "deutsche", label: "Deutsche Bank (Niemcy)" },
  { key: "commerzbank", label: "Commerzbank (Niemcy)" },
  { key: "n26", label: "N26 (Niemcy)" },
  { key: "dkb", label: "DKB — Deutsche Kreditbank (Niemcy)" },
  // Francja
  { key: "bnp-fr", label: "BNP Paribas (Francja)" },
  { key: "ca-fr", label: "Crédit Agricole (Francja)" },
  { key: "socgen", label: "Société Générale (Francja)" },
  // Hiszpania
  { key: "santander-es", label: "Banco Santander (Hiszpania)" },
  { key: "bbva", label: "BBVA (Hiszpania)" },
  { key: "caixabank", label: "CaixaBank (Hiszpania)" },
  // Włochy
  { key: "unicredit", label: "UniCredit (Włochy)" },
  { key: "intesa", label: "Intesa Sanpaolo (Włochy)" },
  // Holandia
  { key: "ing-nl", label: "ING (Holandia)" },
  { key: "abnamro", label: "ABN AMRO (Holandia)" },
  { key: "rabobank", label: "Rabobank (Holandia)" },
  // Pozostałe
  { key: "kbc", label: "KBC Bank (Belgia)" },
  { key: "erste-at", label: "Erste Bank (Austria)" },
  { key: "raiffeisen-at", label: "Raiffeisen Bank (Austria)" },
  { key: "nordea", label: "Nordea (Finlandia/Skandynawia)" },
  { key: "danske", label: "Danske Bank (Dania)" },
  { key: "csob-cz", label: "ČSOB (Czechy)" },
  { key: "otp-hu", label: "OTP Bank (Węgry)" },
];

const findBank = (key: string): BankOption | undefined =>
  EU_BANKS.find((b) => b.key === key);

// Pakiety biurowe — integracja dobrana wg nazwy pakietu w wierszu.
const OFFICE_PACKAGES: {
  key: string;
  label: string;
  kind: OfficeKind;
  Icon: SvgIcon;
}[] = [
  {
    key: "outlook-cal",
    label: "Outlook calendar",
    kind: "microsoft-calendar",
    Icon: OutlookIcon,
  },
  {
    key: "google-cal",
    label: "Google calendar",
    kind: "google-calendar",
    Icon: GoogleIcon,
  },
  {
    key: "apple-cal",
    label: "Apple calendar",
    kind: "apple-calendar",
    Icon: AppleIcon,
  },
  {
    key: "google-docs",
    label: "Google docs",
    kind: "google-docs",
    Icon: DocsIcon,
  },
  {
    key: "office365",
    label: "Office 365",
    kind: "office365",
    Icon: MicrosoftIcon,
  },
];

// Komunikat o przeznaczeniu zakładki.
const DESCRIPTIONS: Partial<Record<SubTab, string>> = {
  active:
    "W tej zakładce znajdziesz filmiki instruktażowe dotyczące poszczególnych typów integracji — krok po kroku pokazują, jak skonfigurować i obsługiwać każdą z dostępnych integracji.",
  public:
    "W tej zakładce skonfigurujesz integracje z publicznymi portalami i usługami sądowo-skarbowymi: Portalem Informacyjnym sądów powszechnych oraz Krajowym Systemem e-Faktur (KSeF).",
  nonpublic:
    "W tej zakładce skonfigurujesz integracje z systemami bankowymi, umożliwiające pobieranie i uzgadnianie danych transakcyjnych.",
  email:
    "W tej zakładce skonfigurujesz integracje z pocztą e-mail (np. Outlook, Gmail, IMAP/POP3), aby odbierać i wysyłać wiadomości bez opuszczania aplikacji.",
  office:
    "W tej zakładce skonfigurujesz integracje z pakietami biurowymi (edytory dokumentów, kalendarze), aby pracować na plikach bezpośrednio w aplikacji.",
};

const EMAIL_ATTEMPTS_KEY = "email_integration_attempts_v1";

const noop = () => {};

const Integrations1Tab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState<SubTab>("active");

  // Wybrana integracja — gdy ustawiona, prezentujemy kreator w oknie slide.
  const [openProvider, setOpenProvider] = useState<PublicProvider | null>(null);
  const closeWizard = () => setOpenProvider(null);

  // Kreator poświadczeń publikacji ogłoszeń (e-Zamówienia API MO) + status konfiguracji.
  const [procConfigOpen, setProcConfigOpen] = useState(false);
  const { data: procCreds } = procurementService.useCredentials();

  // Rozwinięte wiersze integracji (tryb zwiń/rozwiń — szczegóły: NIP, okres ważności).
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Integracja, dla której pokazujemy okno z opisem zakresu usług.
  const [infoOf, setInfoOf] = useState<string | null>(null);
  const infoItem = PUBLIC_INTEGRATIONS.find((x) => x.key === infoOf);

  // Bank, dla którego otwieramy kreator integracji w oknie slide.
  const [openBank, setOpenBank] = useState<string | null>(null);
  const bankItem = openBank ? findBank(openBank) : undefined;

  // Aktywne integracje bankowe pochodzą z BE (zgody/sesje Enable Banking) — ta
  // sama warstwa, z której korzysta rejestr „Operacje finansowe".
  const { data: connectionsRaw } = bankingService.useGetConnections();
  const connections = Array.isArray(connectionsRaw) ? connectionsRaw : [];
  const deleteConnection = bankingService.useDeleteConnection();

  // Dopasowanie banku z listy do zgody po nazwie ASPSP.
  const connectionForBank = (bank: BankOption) =>
    connections.find(
      (c) => c.AspspName?.toLowerCase() === bank.label.toLowerCase(),
    );
  const isBankActive = (bank: BankOption) => !!connectionForBank(bank);
  const handleRemoveBank = (bank: BankOption) => {
    const conn = connectionForBank(bank);
    if (conn) deleteConnection.mutate(conn.Id);
  };

  // Lista wyświetlana = 10 domyślnych banków + aktywne zgody spoza tej dziesiątki
  // (np. bank wskazany wcześniej z wyszukiwarki UE).
  const extraActive: BankOption[] = connections
    .filter(
      (c) =>
        c.AspspName &&
        !BANKS.some((b) => b.label.toLowerCase() === c.AspspName.toLowerCase()),
    )
    .map((c) => ({ key: c.AspspName, label: c.AspspName }))
    .filter(
      (opt, i, arr) => arr.findIndex((x) => x.key === opt.key) === i,
    );
  const displayBanks = [...BANKS, ...extraActive];

  // Aktywne integracje na początku listy (sort stabilny — zachowuje kolejność).
  const sortedBanks = [...displayBanks].sort(
    (a, b) => Number(isBankActive(b)) - Number(isBankActive(a)),
  );

  // Opcje wyszukiwarki = banki UE, których nie ma jeszcze na liście.
  const pickerOptions = EU_BANKS.filter(
    (b) => !displayBanks.some((d) => d.key === b.key),
  );

  // Zakładka Email — integracje pobierane z BE + lokalne próby.
  const { data: integrationsData, refetch: refetchIntegrations } =
    settingsService.useGetIntegrationSettings();

  const activeEmailIntegrations = useMemo(
    () =>
      (integrationsData?.data?.Active ?? []).filter(
        (x) => x.Type === IntegrationType.Email,
      ),
    [integrationsData],
  );

  // Aktywna integracja KSeF (jeśli istnieje) — Name zawiera NIP (GetName w BE).
  const activeKsef = useMemo(
    () =>
      (integrationsData?.data?.Active ?? []).find(
        (x) => x.Type === IntegrationType.KSeF,
      ),
    [integrationsData],
  );

  // Ważność środka autoryzacji KSeF (token) — z ustawień KSeF, gdy integracja aktywna.
  const { data: ksefSettingsResp, refetch: refetchKsef } =
    settingsService.useGetKsefSettings(!activeKsef);
  const ksefAuthValidUntil = ksefSettingsResp?.data?.AuthValidUntil;

  // Aktywna integracja rządowych e-Doręczeń (jeśli istnieje) — Address zawiera adres ADE.
  const activeEpuap = useMemo(
    () =>
      (integrationsData?.data?.Active ?? []).find(
        (x) => x.Type === IntegrationType.Epuap,
      ),
    [integrationsData],
  );

  const activeGovEDelivery = useMemo(
    () =>
      (integrationsData?.data?.Active ?? []).find(
        (x) => x.Type === IntegrationType.GovEDelivery,
      ),
    [integrationsData],
  );

  // Aktywna integracja UPE — Name niesie wystawcę dziennika, Address login.
  const activeUpe = useMemo(
    () =>
      (integrationsData?.data?.Active ?? []).find(
        (x) => x.Type === IntegrationType.Upe,
      ),
    [integrationsData],
  );

  // Portal Informacyjny — stan integracji (login/hasło).
  const { data: courtSettingsResp } =
    settingsService.useGetCourtPortalSettings();
  const courtSettings = courtSettingsResp?.data as ICourtPortalSettings | undefined;

  // Spójny, wspólny dla wszystkich integracji znacznik ważności środka autoryzacji.
  const AuthValidity = ({
    validUntil,
    indefinite,
  }: {
    validUntil?: string | null;
    indefinite?: boolean;
  }) => (
    <span
      style={{
        fontSize: 12,
        marginLeft: 8,
        whiteSpace: "nowrap",
        color: !indefinite && isAuthExpired(validUntil) ? "#BA1A1A" : "#6b7280",
      }}
    >
      {authValidityLabel({ validUntil, indefinite })}
    </span>
  );

  // Stan integracji publicznej:
  //  • canIntegrate — czy udostępniamy dla portalu wykonanie integracji (przycisk),
  //  • active       — czy integracja jest już utworzona,
  //  • param/paramLabel/validUntil/indefinite — dane do rozwijanego elementu.
  // Reguła przycisków: canIntegrate && !active → „+ Dodaj"; canIntegrate && active → „Odśwież".
  // EGiB i e-Zamówienia (odczyt) są zawsze aktywne — działają bez poświadczeń.
  const publicState = (
    key: string,
  ): {
    canIntegrate: boolean;
    active: boolean;
    param?: string | null;
    paramLabel?: string;
    validUntil?: string | null;
    indefinite?: boolean;
  } => {
    if (key === "ksef") {
      return {
        canIntegrate: true,
        active: !!activeKsef,
        param: activeKsef?.Name ?? null,
        paramLabel: "NIP",
        validUntil: ksefAuthValidUntil,
        indefinite: false,
      };
    }
    if (key === "procurement") {
      return {
        canIntegrate: true,
        active: true, // zintegrowane — odczyt ogłoszeń BZP działa bez poświadczeń
        param: procCreds?.IsConfigured
          ? `Odczyt + publikacja (${procCreds.Environment})`
          : "Odczyt ogłoszeń (BZP)",
        paramLabel: "Zakres",
        indefinite: true,
      };
    }
    if (key === "court") {
      return {
        canIntegrate: true,
        active: !!courtSettings?.IsActive,
        param: courtSettings?.Login ?? null,
        paramLabel: "Login",
        indefinite: true,
      };
    }
    if (key === "gov-edelivery") {
      // Integracja na certyfikacie (PFX) — pokazujemy datę ważności certyfikatu (NotAfter).
      return {
        canIntegrate: true,
        active: !!activeGovEDelivery,
        param: activeGovEDelivery?.Address ?? activeGovEDelivery?.Name ?? null,
        paramLabel: "Adres ADE",
        validUntil: activeGovEDelivery?.CertValidUntil,
        indefinite: !activeGovEDelivery?.CertValidUntil,
      };
    }
    if (key === "epuap") {
      // Integracja na certyfikacie (PFX) — pokazujemy datę ważności certyfikatu (NotAfter).
      return {
        canIntegrate: true,
        active: !!activeEpuap,
        param: activeEpuap?.Address ?? activeEpuap?.Name ?? null,
        paramLabel: "Skrytka",
        validUntil: activeEpuap?.CertValidUntil,
        indefinite: !activeEpuap?.CertValidUntil,
      };
    }
    if (key === "upe") {
      // Integracja na loginie i haśle — bez certyfikatu, więc bez daty ważności.
      return {
        canIntegrate: true,
        active: !!activeUpe,
        param: activeUpe?.Name ?? null,
        paramLabel: "Wystawca dziennika",
        indefinite: true,
      };
    }
    if (key === "egib") {
      return {
        canIntegrate: true,
        active: true, // dane przestrzenne GUGiK dostępne bez poświadczeń
        param: "GUGiK — ULDK / WMS",
        paramLabel: "Źródło danych",
        indefinite: true,
      };
    }
    return { canIntegrate: false, active: false };
  };

  // Sortowanie listy: najpierw aktywne, potem możliwe do integracji, na końcu portale
  // bez możliwości integracji. W obrębie grupy zachowana kolejność z definicji.
  const sortedPublicIntegrations = useMemo(() => {
    const rank = (key: string) => {
      const s = publicState(key);
      if (s.active) return 0;
      if (s.canIntegrate) return 1;
      return 2;
    };
    return PUBLIC_INTEGRATIONS
      .map((it, i) => ({ it, i, r: rank(it.key) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.it);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKsef, courtSettings, procCreds, activeGovEDelivery, activeEpuap, activeUpe]);

  const [attemptedEmails, setAttemptedEmails] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(EMAIL_ATTEMPTS_KEY) ?? "[]"); }
    catch { return []; }
  });

  const recordAttempt = (email: string) => {
    setAttemptedEmails((prev) => {
      if (prev.includes(email)) return prev;
      const next = [...prev, email];
      localStorage.setItem(EMAIL_ATTEMPTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const removeAttempt = (email: string) => {
    setAttemptedEmails((prev) => {
      const next = prev.filter((e) => e !== email);
      localStorage.setItem(EMAIL_ATTEMPTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const failedEmails = useMemo(
    () => attemptedEmails.filter(
      (e) => !activeEmailIntegrations.some((x) => x.Name.toLowerCase() === e.toLowerCase()),
    ),
    [attemptedEmails, activeEmailIntegrations],
  );

  const [newEmailValue, setNewEmailValue] = useState("");
  const [openEmail, setOpenEmail] = useState<string | null>(null);

  // Zakładka Pakiety biurowe — slide z kreatorem dla wybranego pakietu.
  const [openOffice, setOpenOffice] = useState<string | null>(null);
  const officeItem = OFFICE_PACKAGES.find((o) => o.key === openOffice);

  const providerLabel =
    PUBLIC_INTEGRATIONS.find((x) => x.key === openProvider)?.label ?? "";

  const renderWizard = () => {
    if (openProvider === "court") {
      // Kreator integracji z Portalem Informacyjnym sądów powszechnych.
      return (
        <CourtPortalSettings
          integrationId={0}
          onCanceled={closeWizard}
          onSaved={closeWizard}
          handleDisconnect={noop}
          refetchI={noop}
          refetchportals={noop}
          grow
          createOnly
        />
      );
    }
    if (openProvider === "ksef") {
      // Kreator integracji z KSeF. Po sukcesie odświeżamy listę integracji ORAZ
      // ustawienia KSeF (AuthValidUntil) — inaczej po ponownym wgraniu certyfikatu
      // status pozostałby „Wygasła" na starej dacie ważności z cache.
      return (
        <ConfigureKSeF
          onSuccess={() => {
            refetchIntegrations();
            refetchKsef();
            closeWizard();
          }}
          toggle={closeWizard}
        />
      );
    }
    if (openProvider === "gov-edelivery") {
      // Kreator integracji z rządowymi e-Doręczeniami (Poczta Polska).
      return (
        <ConfigureEDelivery
          onSuccess={() => {
            refetchIntegrations();
            closeWizard();
          }}
          toggle={closeWizard}
        />
      );
    }
    if (openProvider === "epuap") {
      // Kreator integracji z ePUAP.
      return (
        <ConfigureEpuap
          onSuccess={() => {
            refetchIntegrations();
            closeWizard();
          }}
          toggle={closeWizard}
        />
      );
    }
    if (openProvider === "upe") {
      // Kreator integracji z Urzędową Pocztą Elektroniczną (dzienniki urzędowe).
      return (
        <ConfigureUpe
          onSuccess={() => {
            refetchIntegrations();
            closeWizard();
          }}
          toggle={closeWizard}
        />
      );
    }
    return null;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.subTabs}>
        {SUB_TABS.map((tab) => (
          <div
            key={tab.key}
            className={`${styles.subTab} ${
              subTab === tab.key ? styles.subTabActive : ""
            }`}
            onClick={() => {
              setSubTab(tab.key);
              setOpenProvider(null);
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className={styles.content}>
        {subTab === "active" && (
          <div className={styles.panel}>
            <div className={styles.intDesc}>{DESCRIPTIONS.active}</div>
            <IntegrationVideos />
          </div>
        )}

        {subTab === "public" && (
          <div className={styles.panel}>
            <div className={styles.intDesc}>{DESCRIPTIONS.public}</div>
            <div className={styles.intList}>
              {sortedPublicIntegrations.map((item) => {
                const { Icon, IconImg } = item;
                const st = publicState(item.key);
                // Wygasła / wymaga odnowienia (certyfikat/token).
                const expired = st.active && !st.indefinite && isAuthExpired(st.validUntil);
                // Rozwijany element ma sens, gdy integracja jest ustanowiona.
                const hasDetails = st.active;
                const expanded = expandedKeys.has(item.key);

                // „+ Dodaj" — utworzenie integracji (kreator / konfiguracja poświadczeń).
                const onAdd = () => {
                  if (item.connectable) setOpenProvider(item.connectable);
                  else if (item.key === "procurement") setProcConfigOpen(true);
                };
                // „Odśwież" — odnowienie istniejącej integracji. Realizuje akcje, które
                // wcześniej dublowały się w rozwijanym elemencie („Uwierzytelnij ponownie",
                // „Edytuj poświadczenia"): dla integracji z kreatorem (KSeF, PI) otwiera
                // kreator (KSeF: ponowne wczytanie certyfikatu i klucza), dla e-Zamówień —
                // konfigurację poświadczeń, dla EGiB (bez poświadczeń) — widok mapy.
                const onRefresh = () => {
                  if (item.connectable) setOpenProvider(item.connectable);
                  else if (item.key === "procurement") setProcConfigOpen(true);
                  else if (item.key === "egib") navigate("/egib");
                };

                // KOLUMNA 4 — dokładnie jeden przycisk (nigdy dwa naraz):
                //  • „+ Dodaj"  — gdy integracja możliwa, ale jeszcze nieutworzona,
                //  • „Odśwież"  — gdy integracja jest już utworzona (aktywna).
                let action: JSX.Element | null = null;
                if (st.canIntegrate) {
                  action = st.active ? (
                    <button className={`${styles.addBtn} ${styles.refreshBtn}`} onClick={onRefresh}>
                      Odśwież
                    </button>
                  ) : (
                    <button className={styles.addBtn} onClick={onAdd}>
                      + Dodaj
                    </button>
                  );
                }

                return (
                  <div key={item.key} className={styles.pubItem}>
                    <div className={styles.pubRow4}>
                      {/* KOL. 1 — ikona informacji o usłudze */}
                      <span
                        className={styles.intInfo}
                        title="Zakres usług tej integracji"
                        onClick={() => setInfoOf(item.key)}
                      >
                        <InfoIcon />
                      </span>

                      {/* KOL. 2 — logo portalu */}
                      {IconImg ? (
                        <span className={styles.intLogo}>
                          <img src={IconImg} alt={item.label} />
                        </span>
                      ) : Icon ? (
                        <span className={styles.intLogo}><Icon /></span>
                      ) : (
                        <span className={styles.intLogoEmpty} />
                      )}

                      {/* KOL. 3 — nazwa portalu; w 2. wierszu chip statusu + ikona uwag */}
                      <div className={styles.pubNameCell}>
                        <div className={styles.pubNameTop}>
                          {/* Nazwa bez zawijania — ucinana z wielokropkiem przy braku miejsca. */}
                          <span className={styles.intName} title={item.label}>{item.label}</span>
                          {hasDetails && (
                            <button
                              type="button"
                              className={styles.expandBtn}
                              aria-label={expanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
                              onClick={() => toggleExpanded(item.key)}
                            >
                              <ExpandMoreIcon
                                fontSize="small"
                                className={expanded ? styles.chevUp : undefined}
                              />
                            </button>
                          )}
                        </div>
                        {(st.active || item.notes) && (
                          <div className={styles.pubNameMeta}>
                            {st.active && (
                              <span className={`${styles.statusDot} ${expired ? styles.statusDotExpired : ""}`}>
                                {expired ? "Wygasła" : "Aktywna"}
                              </span>
                            )}
                            {item.notes && (
                              <Tooltip title={item.notes} arrow>
                                <span className={styles.pubNotes}>
                                  <ReportProblemOutlinedIcon fontSize="small" />
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>

                      {/* KOL. 4 — jeden przycisk: + Dodaj albo Odśwież */}
                      <span className={styles.pubAction}>{action}</span>
                    </div>

                    {/* Rozwijany element — parametr integracji + okres ważności */}
                    {hasDetails && expanded && (
                      <div className={styles.pubDetails}>
                        {st.param && (
                          <div className={styles.pubDetailRow}>
                            <span className={styles.pubDetailKey}>{st.paramLabel ?? "Identyfikator"}</span>
                            <span className={styles.pubDetailVal}>{st.param}</span>
                          </div>
                        )}
                        <div className={styles.pubDetailRow}>
                          <span className={styles.pubDetailKey}>Okres ważności</span>
                          <span className={`${styles.pubDetailVal} ${expired ? styles.pubDetailExpired : ""}`}>
                            {authValidityLabel({ validUntil: st.validUntil, indefinite: st.indefinite })}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {subTab === "nonpublic" && (
          <div className={styles.panel}>
            <div className={styles.intDesc}>{DESCRIPTIONS.nonpublic}</div>
            <div className={styles.intList}>
              {sortedBanks.map((bank) => {
                const active = isBankActive(bank);
                return (
                  <div key={bank.key} className={styles.intRow}>
                    <span className={styles.intName}>{bank.label}</span>
                    <span
                      className={`${styles.statusBadge} ${
                        active ? styles.statusActive : styles.statusInactive
                      }`}
                    >
                      {active ? "Aktywna" : "Nieaktywna"}
                    </span>
                    {active && (
                      // Zgoda AIS (PSD2) — typowo 90 dni; ValidUntil z połączenia.
                      <AuthValidity validUntil={connectionForBank(bank)?.ValidUntil} />
                    )}
                    {active ? (
                      <button
                        className={`${styles.addBtn} ${styles.removeBtn}`}
                        onClick={() => handleRemoveBank(bank)}
                      >
                        Usuń integrację
                      </button>
                    ) : (
                      <button
                        className={styles.addBtn}
                        onClick={() => setOpenBank(bank.key)}
                      >
                        {t("integrations.addIntegration")}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className={styles.bankPickerRow}>
              <Autocomplete
                options={pickerOptions}
                getOptionLabel={(o) => o.label}
                value={null}
                blurOnSelect
                onChange={(_, option) => {
                  if (option) setOpenBank(option.key);
                }}
                noOptionsText="Brak banku w katalogu"
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Nie ma Twojego banku na liście? Wyszukaj bank z UE…"
                  />
                )}
              />
            </div>
          </div>
        )}
        {subTab === "email" && (
          <div className={styles.panel}>
            <div className={styles.intDesc}>{DESCRIPTIONS.email}</div>
            <div className={styles.intList}>
              {activeEmailIntegrations.map((integration) => (
                <div key={integration.Id} className={styles.intRow}>
                  <div className={styles.emailField}>
                    <input
                      type="text"
                      className={styles.emailInput}
                      value={integration.Name}
                      readOnly
                    />
                  </div>
                  <span className={`${styles.statusBadge} ${styles.statusActive}`}>
                    Aktywny
                  </span>
                  {/* Poświadczenia poczty (login/hasło) — ważne do zmiany hasła. */}
                  <AuthValidity indefinite />
                </div>
              ))}
              {failedEmails.map((email) => (
                <div key={email} className={styles.intRow}>
                  <div className={styles.emailField}>
                    <input
                      type="text"
                      className={styles.emailInput}
                      value={email}
                      readOnly
                    />
                  </div>
                  <span className={`${styles.statusBadge} ${styles.statusInactive}`}>
                    Nieaktywny
                  </span>
                  <button
                    className={`${styles.addBtn} ${styles.removeBtn}`}
                    onClick={() => removeAttempt(email)}
                  >
                    Usuń
                  </button>
                </div>
              ))}
              <div className={styles.intRow}>
                <div className={styles.emailField}>
                  <input
                    type="text"
                    inputMode="email"
                    name="mbx-new"
                    autoComplete="mbx-nope-new"
                    data-lpignore="true"
                    data-form-type="other"
                    className={styles.emailInput}
                    placeholder="Wpisz swój adres e-mail"
                    value={newEmailValue}
                    onChange={(e) => setNewEmailValue(e.target.value)}
                  />
                  {newEmailValue && (
                    <button
                      type="button"
                      className={styles.emailClear}
                      onClick={() => setNewEmailValue("")}
                      title="Wyczyść"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <button
                  className={styles.addBtn}
                  disabled={!newEmailValue.trim()}
                  onClick={() => {
                    const email = newEmailValue.trim();
                    recordAttempt(email);
                    setOpenEmail(email);
                  }}
                >
                  {t("integrations.addIntegration")}
                </button>
              </div>
            </div>
          </div>
        )}
        {subTab === "office" && (
          <div className={styles.panel}>
            <div className={styles.intDesc}>{DESCRIPTIONS.office}</div>
            <div className={styles.intList}>
              {OFFICE_PACKAGES.map((pkg) => {
                const { Icon } = pkg;
                return (
                  <div key={pkg.key} className={styles.intRow}>
                    <span className={styles.intLogo}>
                      <Icon />
                    </span>
                    <span className={styles.intName}>{pkg.label}</span>
                    <button
                      className={styles.addBtn}
                      onClick={() => setOpenOffice(pkg.key)}
                    >
                      {t("integrations.addIntegration")}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Kreator integracji w oknie slide (panel od prawej krawędzi). */}
      {openProvider &&
        createPortal(
          <div
            className={styles.slideOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeWizard();
            }}
          >
            <div className={styles.slidePanel}>
              <div className={styles.slideHeader}>
                <span className={styles.slideTitle}>
                  Dodaj integrację — {providerLabel}
                </span>
                <button
                  className={styles.slideClose}
                  onClick={closeWizard}
                  title="Zamknij"
                >
                  ✕
                </button>
              </div>
              <div className={styles.slideBody}>{renderWizard()}</div>
            </div>
          </div>,
          document.body,
        )}

      {/* Okno z syntetycznym opisem zakresu usług danej integracji. */}
      {infoItem &&
        createPortal(
          <div
            className={styles.infoOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setInfoOf(null);
            }}
          >
            <div className={styles.infoCard}>
              <div className={styles.infoHeader}>
                <span className={styles.infoTitle}>{infoItem.label}</span>
                <button
                  className={styles.infoClose}
                  onClick={() => setInfoOf(null)}
                  title="Zamknij"
                >
                  ✕
                </button>
              </div>
              <div className={styles.infoBody}>{infoItem.info}</div>
            </div>
          </div>,
          document.body,
        )}

      {/* Kreator integracji bankowej (PolishAPI / PSD2) w oknie slide. */}
      {bankItem &&
        createPortal(
          <div
            className={styles.slideOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpenBank(null);
            }}
          >
            <div className={styles.slidePanel}>
              <div className={styles.slideHeader}>
                <span className={styles.slideTitle}>
                  Dodaj integrację — {bankItem.label}
                </span>
                <button
                  className={styles.slideClose}
                  onClick={() => setOpenBank(null)}
                  title="Zamknij"
                >
                  ✕
                </button>
              </div>
              <div className={styles.slideBody}>
                <BankIntegrationWizard
                  bankName={bankItem.label}
                  aspspName={bankItem.label}
                  country="PL"
                  onClose={() => setOpenBank(null)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Kreator integracji skrzynki e-mail w oknie slide. */}
      {openEmail &&
        createPortal(
          <div
            className={styles.slideOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpenEmail(null);
            }}
          >
            <div className={styles.slidePanel}>
              <div className={styles.slideHeader}>
                <span className={styles.slideTitle}>
                  Dodaj integrację — skrzynka e-mail
                </span>
                <button
                  className={styles.slideClose}
                  onClick={() => setOpenEmail(null)}
                  title="Zamknij"
                >
                  ✕
                </button>
              </div>
              <div className={styles.slideBody}>
                <EmailIntegrationWizard
                  email={openEmail}
                  onConnected={() => {
                    removeAttempt(openEmail);
                    refetchIntegrations();
                    setNewEmailValue("");
                    setOpenEmail(null);
                  }}
                  onClose={() => setOpenEmail(null)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Kreator integracji pakietu biurowego w oknie slide. */}
      {officeItem &&
        createPortal(
          <div
            className={styles.slideOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpenOffice(null);
            }}
          >
            <div className={styles.slidePanel}>
              <div className={styles.slideHeader}>
                <span className={styles.slideTitle}>
                  Dodaj integrację — {officeItem.label}
                </span>
                <button
                  className={styles.slideClose}
                  onClick={() => setOpenOffice(null)}
                  title="Zamknij"
                >
                  ✕
                </button>
              </div>
              <div className={styles.slideBody}>
                <OfficeIntegrationWizard
                  packageLabel={officeItem.label}
                  kind={officeItem.kind}
                  onConnected={() => setOpenOffice(null)}
                  onClose={() => setOpenOffice(null)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}

      <ConfigureProcurement open={procConfigOpen} onClose={() => setProcConfigOpen(false)} />
    </div>
  );
};

export default Integrations1Tab;
